import {
  colorOrange,
  colorPurple,
  colorYellow,
} from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  Bullet,
  DataObjectGroupProcessingStateType,
  formatDateTimeShort,
  SystemClassifierItemDto,
  Tooltip,
} from '@atlas-frontend/sdk';
import { CellContext } from '@tanstack/react-table';
import { Flex } from 'antd';
import translations from './ActiveProcessingType.translations.json';
import { FC, memo } from 'react';

const { addTranslations, t } = i18nNsFactory('active-processing-type');
addTranslations(translations);

const LABEL_DELIMITER = ' - ';

type ActiveProcessingState = Pick<
  DataObjectGroupProcessingStateType,
  'processing'
>;

export const getActiveProcessingTypeLabel = ({
  processingState,
  processingTypes,
  processingMethods,
  processingScopes,
}: {
  processingState: ActiveProcessingState;
  processingTypes: SystemClassifierItemDto[];
  processingMethods: SystemClassifierItemDto[];
  processingScopes: SystemClassifierItemDto[];
}) => {
  const { processing } = processingState;
  const isCurrent =
    processing?.phaseCode === 'PENDING' || processing?.phaseCode === 'ACTIVE';

  if (!isCurrent) return '';

  const {
    processingTypeId,
    processingMethodId,
    dataProcessingScopeId,
    processingTime,
    processingStartedByUsername,
  } = processing;

  const type = processingTypes.find((type) => type.id === processingTypeId);
  const method =
    processingMethodId === null
      ? undefined
      : processingMethods.find((method) => method.id === processingMethodId);
  const scope =
    dataProcessingScopeId === null
      ? undefined
      : processingScopes.find((scope) => scope.id === dataProcessingScopeId);

  if (!type) {
    return '';
  }

  return [
    t('label'),
    type.code,
    type.name,
    formatDateTimeShort(processingTime),
    processingStartedByUsername,
    t('method.' + method?.code, { scope: scope?.name }),
  ].join(LABEL_DELIMITER);
};

const getBulletColor = (code: string) => {
  switch (code) {
    case 'IMPUTE_DATA':
      return colorOrange[5];
    case 'LOAD_SAMPLE':
      return colorPurple[5];
    default:
      return colorYellow[5];
  }
};

export const ActiveProcessingType: FC<
  CellContext<DataObjectGroupProcessingStateType, any>
> = memo((props) => {
  const delimitedLabel = props.renderValue();

  if (!delimitedLabel) {
    return null;
  }

  const [
    label,
    typeCode,
    typeName,
    startTime,
    startedByUsername,
    methodAndScope,
  ] = delimitedLabel.split(LABEL_DELIMITER);

  return (
    <Tooltip
      title={
        <>
          <strong>{typeName}</strong>
          <br />
          {startTime} ({startedByUsername})
          <br />
          {methodAndScope}
        </>
      }
      placement="bottom-start"
    >
      <Flex align="center" gap={6}>
        <Bullet color={getBulletColor(typeCode)} />
        {label}
      </Flex>
    </Tooltip>
  );
});
